import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeOff, UserPlus, Check, Loader2, X, Sprout, Rocket, Crown } from 'lucide-react';
import { PackageType } from '@/types';
import { API_BASE as API_BASE_URL } from '@/services/apiClient';
import aivLogo from '@/assets/aiv-logo-trans.png';
import { useI18n } from '@/contexts/I18nContext';

const packages = [
  {
    id: 'package1',
    name: 'Starter Package',
    monthlyPrice: 69,
    yearlyDiscountPct: 5,
    icon: Sprout,
    features: [
      'Up to 50 active members',
      'Member management',
      'Queue and court assignment',
      'Match history and basic leaderboard',
      'Basic admin dashboard',
    ],
  },
  {
    id: 'package2',
    name: 'Pro',
    monthlyPrice: 159,
    yearlyDiscountPct: 8,
    icon: Rocket,
    features: [
      'Everything in Starter',
      'Up to 150 active members',
      'Coach evaluations',
      'Equipment tracking',
      'Limited ML-assisted analytics for key performance trends',
      'Actionable performance insights on your dashboard to help members improve faster.',
      'Daily match reports and advanced leaderboard trends',
    ],
  },
  {
    id: 'package3',
    name: 'Elite',
    monthlyPrice: 299,
    yearlyDiscountPct: 10,
    icon: Crown,
    features: [
      'Everything in Pro',
      'Up to 250 active members',
      'Advanced roles and permissions, including multiple club admins',
      "Advanced UI controls to customize your app's colors and fonts for your club brand.",
      'Upload and manage your club logo for branded club pages.',
      'Members can choose profile avatars for a more personalized experience.',
      'Chat with AI to uncover hidden patterns and insights from your match history',
      'Actionable performance insights on your dashboard to help members improve faster.',
    ],
  },
];

type Step = 1 | 2 | 3;

const API_BASE = API_BASE_URL;

// FastAPI can return {detail: "..."} OR {detail: [{msg: "..."}]}
async function parseApiError(res: Response) {
  const data = await res.json().catch(() => ({}));
  const detail = data?.detail;

  const msg =
    (Array.isArray(detail) && detail[0]?.msg) ||
    (typeof detail === 'string' && detail) ||
    data?.error ||
    'Request failed';

  return { data, msg };
}

export default function Signup() {
  const { t } = useI18n();
  const [step, setStep] = useState<Step>(1);

  // Account info
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [clubName, setClubName] = useState('');
  const [sportType, setSportType] = useState('');
  const [email, setEmail] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeTermsAndConditions, setAgreeTermsAndConditions] = useState(false);

  const [showPassword, setShowPassword] = useState(false);

  // Package
  const [packageType, setPackageType] = useState<PackageType>('package1');
  const [subscribeYearly, setSubscribeYearly] = useState(false);

  // OTP
  const [otp, setOtp] = useState('');
  const [otpSessionId, setOtpSessionId] = useState<number | null>(null);
  const [otpVerified, setOtpVerified] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [isOtpLoading, setIsOtpLoading] = useState(false);

  // Final submit loading
  const [isLoading, setIsLoading] = useState(false);

  const { signup } = useAuth();
  const navigate = useNavigate();
  const selectedPackage = packages.find((p) => p.id === packageType) || packages[0];
  const regularYearlyTotal = selectedPackage.monthlyPrice * 12;
  const discountedYearlyTotal = Math.round(
    regularYearlyTotal * (1 - selectedPackage.yearlyDiscountPct / 100)
  );
  const yearlySavings = regularYearlyTotal - discountedYearlyTotal;
  const formatCad = (amount: number) => `CA$${new Intl.NumberFormat('en-CA', { maximumFractionDigits: 0 }).format(amount)}`;

  const hasMinLength = password.length >= 8;
  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasDigit = /[0-9]/.test(password);
  const hasSpecial = /[^A-Za-z0-9]/.test(password);
  const isPasswordValid = hasMinLength && hasUpper && hasLower && hasDigit && hasSpecial;
  const passwordsMatch = password === confirmPassword;
  const showPasswordMismatch = confirmPassword.length > 0 && !passwordsMatch;
  const passwordChecks = [
    { key: 'length', label: t('first_login_at_least_8', 'At least 8 characters'), ok: hasMinLength },
    { key: 'upper', label: t('first_login_at_least_1_upper', 'At least one uppercase letter'), ok: hasUpper },
    { key: 'lower', label: t('first_login_at_least_1_lower', 'At least one lowercase letter'), ok: hasLower },
    { key: 'digit', label: t('first_login_at_least_1_number', 'At least one number'), ok: hasDigit },
    { key: 'special', label: t('first_login_at_least_1_special', 'At least one special character'), ok: hasSpecial },
  ];

  // Countdown for resend
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const validateAccountInfo = () => {
    if (!firstName.trim() || !lastName.trim()) {
      toast.error(t('signup_first_last_required', 'First name and last name are required'));
      return false;
    }

    if (!clubName.trim()) {
      toast.error(t('signup_club_required', 'Club name is required'));
      return false;
    }

    if (!sportType) {
      toast.error(t('signup_select_sport_required', 'Please select a sport type'));
      return false;
    }

    if (!email.trim()) {
      toast.error(t('signup_email_required', 'Email is required'));
      return false;
    }

    if (password !== confirmPassword) {
      toast.error(t('signup_passwords_mismatch', 'Passwords do not match'));
      return false;
    }

    if (!isPasswordValid) {
      toast.error(t('signup_password_requirements', 'Password must meet all password requirements'));
      return false;
    }

    if (!agreePrivacy || !agreeTermsAndConditions) {
      toast.error(t('signup_accept_required', 'Please agree to the privacy policy and terms and conditions to continue'));
      return false;
    }

    return true;
  };

  const resetOtpState = () => {
    setOtp('');
    setOtpSessionId(null);
    setOtpVerified(false);
    setResendIn(0);
    setIsOtpLoading(false);
  };

  const requestOtp = async (): Promise<boolean> => {
    const cleanEmail = email.trim();
    if (!cleanEmail) {
      toast.error(t('signup_email_required', 'Email is required'));
      return false;
    }

    setIsOtpLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/request-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: cleanEmail,
          first_name: firstName,
          last_name: lastName,
        }),
      });

      if (!res.ok) {
        const { msg } = await parseApiError(res);
        throw new Error(msg);
      }

      const data = await res.json().catch(() => ({}));
      const id = data?.otp_session_id;

      if (typeof id !== 'number') {
        throw new Error('Missing otp_session_id from server');
      }

      setOtpSessionId(id);
      setResendIn(60);
      toast.success(t('signup_otp_sent', 'OTP sent to your email'));
      return true;
    } catch (e: any) {
      toast.error(e?.message || t('signup_otp_send_failed', 'Could not send OTP'));
      return false;
    } finally {
      setIsOtpLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!otpSessionId) {
      toast.error(t('signup_otp_session_missing', 'OTP session missing. Please resend OTP.'));
      return;
    }

    const cleanOtp = otp.trim();
    if (cleanOtp.length < 4) {
      toast.error(t('signup_enter_otp', 'Enter the OTP'));
      return;
    }

    setIsOtpLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          otp_session_id: otpSessionId,
          otp: cleanOtp,
        }),
      });

      if (!res.ok) {
        const { msg } = await parseApiError(res);
        throw new Error(msg);
      }

      const data = await res.json().catch(() => ({}));
      if (data?.verified !== true) {
        throw new Error(t('signup_otp_verification_failed', 'OTP verification failed'));
      }

      setOtpVerified(true);
      toast.success(t('signup_email_verified_continue', 'Email verified. Click Continue to proceed.'));
    } catch (e: any) {
      toast.error(e?.message || t('signup_otp_verification_failed', 'OTP verification failed'));
      setOtpVerified(false);
    } finally {
      setIsOtpLoading(false);
    }
  };

  const handleNextFromAccount = async () => {
    if (!validateAccountInfo()) return;

    resetOtpState();
    const sent = await requestOtp();
    if (sent) {
      setStep(2);
    }
  };

  const handleContinueFromOtp = async () => {
    if (otpVerified) {
      setStep(3);
      return;
    }
    await verifyOtp();
  };

  const submitSignup = async () => {
    if (!validateAccountInfo()) {
      setStep(1);
      return;
    }

    if (!otpVerified || !otpSessionId) {
      toast.error(t('signup_verify_email_first', 'Please verify your email first'));
      setStep(2);
      return;
    }

    setIsLoading(true);

    try {
      //  IMPORTANT: send snake_case for backend SignupSchema
      const result = await signup({
        email: email.trim(),
        password,
        first_name: firstName,
        last_name: lastName,
        club_name: clubName.trim(),
        sport_type: sportType,
        package: packageType,          // "package1" => backend upper() => "PACKAGE1"
        is_yearly_plan: subscribeYearly,
        yearly_discount_pct: subscribeYearly ? selectedPackage.yearlyDiscountPct : undefined,
        yearly_price_at_signup: subscribeYearly ? discountedYearlyTotal : undefined,
        otp_session_id: otpSessionId ?? undefined,
        accept_privacy_policy: agreePrivacy,
        accept_data_collection: agreeTermsAndConditions,
        accept_terms_and_conditions: agreeTermsAndConditions,
      });

      if (result.success) {
        const billingCycle = subscribeYearly ? 'yearly' : 'monthly';
        if (result.token) {
          try {
            const checkoutRes = await fetch(`${API_BASE}/billing/checkout-session`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${result.token}`,
              },
              body: JSON.stringify({
                package: packageType,
                billing_cycle: billingCycle,
              }),
            });
            if (checkoutRes.ok) {
              const checkoutData = await checkoutRes.json();
              if (checkoutData?.checkout_url) {
                window.location.href = checkoutData.checkout_url;
                return;
              }
            } else {
              const { msg } = await parseApiError(checkoutRes);
              toast.error(`Account created, but billing checkout failed: ${msg}`);
            }
          } catch {
            toast.error('Account created, but billing checkout could not be started.');
          }
        }

        toast.success(t('signup_account_created_login', 'Account created. Please log in to continue.'));
        navigate('/login');
      } else {
        toast.error(result.error || t('signup_failed', 'Signup failed'));
      }
    } catch (error: any) {
      toast.error(error?.message || t('common_error_occurred', 'An error occurred'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (step === 1) {
      handleNextFromAccount();
    } else if (step === 2) {
      handleContinueFromOtp();
    } else {
      submitSignup();
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-background to-accent/10 p-4">
      <div className="w-full max-w-md md:max-w-3xl lg:max-w-4xl animate-fade-in">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center rounded-2xl text-primary-foreground mb-4">
            <img style={{height:"130px", width:"130px"}} src={aivLogo} alt="AIV Logo" className="object-contain" />
          </div>
          <h1 className="text-4xl md:text-5xl font-display font-bold text-foreground">SmashLytics</h1>
          <p className="text-muted-foreground mt-2 text-sm md:text-lg">{t('signup_title', 'Create your account')}</p>
        </div>

        <Card className="border-border/50 shadow-xl">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl md:text-4xl font-display">
              {step === 1
                ? t('signup_register_club', 'Register your club')
                : step === 2
                  ? t('signup_email_verification', 'Email verification')
                  : t('signup_choose_package', 'Choose your package')}
            </CardTitle>
            <CardDescription className="text-sm md:text-lg">
              {step === 1
                ? t('signup_create_admin_account', 'Create your club admin account to get started')
                : step === 2
                  ? t('signup_enter_otp_email', 'Enter the OTP sent to your email')
                  : t('signup_pick_plan', 'Pick the plan that fits your club best')}
            </CardDescription>
          </CardHeader>

          <CardContent>
            {/* Step indicator */}
            <div className="flex items-center justify-center gap-3 mb-5 text-xs md:text-sm font-medium">
              <div className={`flex items-center gap-2 ${step === 1 ? 'text-primary' : 'text-muted-foreground'}`}>
                <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[11px] border ${step === 1 ? 'bg-primary text-primary-foreground border-primary' : 'border-border'}`}>
                  1
                </span>
                <span>{t('signup_step_account', 'Account')}</span>
              </div>

              <span className="text-border">...</span>

              <div className={`flex items-center gap-2 ${step === 2 ? 'text-primary' : 'text-muted-foreground'}`}>
                <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[11px] border ${step === 2 ? 'bg-primary text-primary-foreground border-primary' : 'border-border'}`}>
                  2
                </span>
                <span>{t('signup_step_verify', 'Verify')}</span>
              </div>

              <span className="text-border">...</span>

              <div className={`flex items-center gap-2 ${step === 3 ? 'text-primary' : 'text-muted-foreground'}`}>
                <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[11px] border ${step === 3 ? 'bg-primary text-primary-foreground border-primary' : 'border-border'}`}>
                  3
                </span>
                <span>{t('signup_step_package', 'Package')}</span>
              </div>
            </div>

            <form onSubmit={handleFormSubmit} className="space-y-5">
              {/* STEP 1 */}
              {step === 1 && (
                <>
                  <div className="rounded-lg border border-primary/30 bg-primary/10 p-4 space-y-4">
                    <h3 className="text-xl md:text-2xl font-bold text-primary">{t('signup_club_details', 'Club details')}</h3>
                    <p className="text-xs md:text-sm text-muted-foreground">{t('signup_required_fields_note', 'Fields marked with * are required.')}</p>
                    <div className="space-y-2">
                      <Label htmlFor="clubName" className="text-base md:text-lg">{t('signup_club_name', 'Your Club Name *')}</Label>
                      <Input
                        id="clubName"
                        placeholder={t('signup_club_name_placeholder', 'Downtown Racquet Club')}
                        value={clubName}
                        onChange={(e) => setClubName(e.target.value)}
                        required
                        className="h-11 md:h-14 text-base md:text-lg"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="sportType" className="text-base md:text-lg">{t('signup_sport_question', 'Which sport does your club play? *')}</Label>
                      <select
                        id="sportType"
                        value={sportType}
                        onChange={(e) => setSportType(e.target.value)}
                        required
                        className="w-full h-11 md:h-14 rounded-md border border-input bg-background px-3 text-base md:text-lg"
                      >
                        <option value="">{t('signup_select_sport', 'Select sport')}</option>
                        <option value="Badminton">Badminton</option>
                        <option value="Pickleball">Pickleball</option>
                      </select>
                    </div>
                  </div>

                  <div className="rounded-lg border border-primary/30 bg-primary/10 p-4 space-y-4">
                    <h3 className="text-xl md:text-2xl font-bold text-primary">{t('signup_admin_details', 'Admin details')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="firstName" className="text-base md:text-lg">{t('signup_first_name', 'First name')} *</Label>
                        <Input
                          id="firstName"
                          placeholder="Jane"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          required
                          className="h-11 md:h-14 text-base md:text-lg"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="lastName" className="text-base md:text-lg">{t('signup_last_name', 'Last name')} *</Label>
                        <Input
                          id="lastName"
                          placeholder="Doe"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          required
                          className="h-11 md:h-14 text-base md:text-lg"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                    <Label htmlFor="email" className="text-base md:text-lg">{t('signup_email', 'Email')} *</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="your@email.com"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (otpVerified || otpSessionId) resetOtpState();
                        }}
                        required
                        className="h-11 md:h-14 text-base md:text-lg"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="password" className="text-base md:text-lg">{t('signup_password', 'Password')} *</Label>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="********"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          className="h-11 md:h-14 text-base md:text-lg pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      <div className="rounded-md border border-border/70 bg-muted/30 p-3">
                        <ul className="space-y-1.5">
                          {passwordChecks.map((rule) => (
                            <li key={rule.key} className="flex items-center gap-2 text-sm md:text-base">
                              {rule.ok ? (
                                <Check size={14} className="text-success shrink-0" />
                              ) : (
                                <X size={14} className="text-muted-foreground shrink-0" />
                              )}
                              <span className={rule.ok ? 'text-foreground' : 'text-muted-foreground'}>
                                {rule.label}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword" className="text-base md:text-lg">{t('signup_confirm_password', 'Confirm password')} *</Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        placeholder="********"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        className="h-11 md:h-14 text-base md:text-lg"
                      />
                      {showPasswordMismatch && (
                        <p className="text-sm text-destructive">{t('signup_passwords_mismatch', 'Passwords do not match')}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <label className="flex items-center gap-3">
                      <input
                        id="privacy"
                        type="checkbox"
                        checked={agreePrivacy}
                        onChange={(e) => setAgreePrivacy(e.target.checked)}
                        className="h-6 w-6 shrink-0"
                      />
                      <span className="text-muted-foreground text-sm md:text-base">
                        {t('first_login_agree_privacy', 'I agree to the')}{' '}
                        <Link to="/privacy-policy" className="text-primary hover:underline">
                          {t('privacy_title', 'Privacy Policy')}
                        </Link>
                        .
                      </span>
                    </label>

                    <label className="flex items-center gap-3">
                      <input
                        id="terms-and-conditions"
                        type="checkbox"
                        checked={agreeTermsAndConditions}
                        onChange={(e) => setAgreeTermsAndConditions(e.target.checked)}
                        className="h-6 w-6 shrink-0"
                      />
                      <span className="text-muted-foreground text-sm md:text-base">
                        {t('first_login_agree_terms', 'I agree to the')}{' '}
                        <Link to="/terms-and-conditions" className="text-primary hover:underline">
                          {t('terms_title', 'Terms and Conditions')}
                        </Link>
                        .
                      </span>
                    </label>
                  </div>

                  <Button type="submit" className="w-full h-11 md:h-14 text-base md:text-lg" disabled={isOtpLoading || isLoading || !isPasswordValid || !passwordsMatch}>
                    {t('signup_verify_email', 'Verify email')}
                  </Button>
                </>
              )}

              {/* STEP 2 */}
              {step === 2 && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="otp" className="text-base md:text-lg">{t('signup_otp_code', 'OTP Code')}</Label>
                    <div className="flex gap-2">
                      <Input
                        id="otp"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        placeholder={t('auth_enter_code', 'Enter code')}
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="h-11 md:h-14 tracking-widest text-center text-lg md:text-xl"
                      />
                      <Button type="button" variant="outline" className="w-32 md:w-40 h-11 md:h-14 text-base md:text-lg" onClick={verifyOtp} disabled={isOtpLoading || !otpSessionId}>
                        {isOtpLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('signup_step_verify', 'Verify')}
                      </Button>
                    </div>
                    {otpVerified && <p className="text-xs text-success">{t('signup_otp_verified', 'OTP verified')}</p>}

                    <div className="flex items-center justify-between text-sm">
                      <button
                        type="button"
                        className="text-primary hover:underline disabled:opacity-50"
                        disabled={isOtpLoading || resendIn > 0}
                        onClick={requestOtp}
                      >
                        {resendIn > 0 ? `${t('signup_resend_in', 'Resend in')} ${resendIn}s` : t('signup_resend_code', 'Resend code')}
                      </button>

                      <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground disabled:opacity-50"
                        onClick={() => {
                          resetOtpState();
                          setStep(1);
                        }}
                        disabled={isOtpLoading}
                      >
                        {t('signup_change_email', 'Change email')}
                      </button>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button type="button" variant="outline" className="w-1/3 h-11 md:h-14 text-base md:text-lg" onClick={() => setStep(1)} disabled={isOtpLoading}>
                      {t('common_back', 'Back')}
                    </Button>

                    <Button type="submit" className="w-2/3 h-11 md:h-14 text-base md:text-lg" disabled={isOtpLoading || !otpSessionId || !otpVerified}>
                      {isOtpLoading ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" /> {t('login_verifying_otp', 'Verifying OTP...')}
                        </span>
                      ) : (
                        t('signup_continue', 'Continue')
                      )}
                    </Button>
                  </div>
                </>
              )}

              {/* STEP 3 */}
              {step === 3 && (
                <>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-base md:text-lg">Choose your package</Label>
                      <p className="mt-1 text-sm md:text-base text-green-700 font-medium">
                        All packages (monthly and yearly) include a 14-day free trial.
                      </p>
                      <p className="text-xs md:text-sm text-muted-foreground">
                        Cancel anytime before the 14-day trial ends to avoid charges.
                      </p>
                    </div>
                    <div className="space-y-3">
                      {packages.map((pkg) => (
                        <button
                          type="button"
                          key={pkg.id}
                          className={`relative w-full p-4 rounded-lg border-2 text-left cursor-pointer transition-all ${
                            packageType === pkg.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                          }`}
                          onClick={() => setPackageType(pkg.id as PackageType)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="w-full">
                              <div className="flex items-start gap-2 md:gap-3">
                                <span
                                  className={`h-5 w-5 rounded-full border-2 inline-flex items-center justify-center shrink-0 ${
                                    packageType === pkg.id ? 'border-primary bg-primary' : 'border-muted-foreground/50'
                                  }`}
                                  aria-hidden="true"
                                >
                                  {packageType === pkg.id ? <Check size={12} className="text-primary-foreground" /> : null}
                                </span>
                                <pkg.icon className="text-primary shrink-0 h-9 w-9 sm:h-10 sm:w-10 md:h-12 md:w-12" />
                                <div className="min-w-0 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                                  <span className="text-lg sm:text-xl md:text-2xl font-semibold leading-tight">{pkg.name}</span>
                                  <span className="text-green-600 font-bold text-base sm:text-lg md:text-xl whitespace-nowrap">
                                    (CA${pkg.monthlyPrice}/mo)
                                  </span>
                                </div>
                              </div>
                              <div className="ml-6 mt-2 inline-flex rounded-full bg-green-100 px-3 py-1 text-xs sm:text-sm font-semibold text-green-700">
                                14-day free trial (monthly & yearly)
                              </div>
                              <ul className="mt-2 ml-6 space-y-1">
                                {pkg.features.map((feature, i) => (
                                  <li key={i} className="text-base md:text-lg text-muted-foreground flex items-center gap-2">
                                    <Check size={14} className="text-success" />
                                    {feature}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>

                    <div className="rounded-lg border border-border/70 bg-muted/30 p-3 sm:p-4 space-y-3">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={subscribeYearly}
                          onChange={(e) => setSubscribeYearly(e.target.checked)}
                          className="h-5 w-5 shrink-0"
                        />
                        <span className="text-sm sm:text-base md:text-lg font-medium leading-snug">
                          Subscribe yearly Plan (save {selectedPackage.yearlyDiscountPct}%)
                        </span>
                      </label>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Yearly plans also include a 14-day free trial. Cancel anytime before trial ends.
                      </p>

                      {subscribeYearly ? (
                        <div className="rounded-md border border-green-600/20 bg-green-50/60 p-3 text-sm sm:text-base md:text-lg">
                          <p className="text-muted-foreground text-sm sm:text-base md:text-lg break-words">
                            {selectedPackage.name}: {selectedPackage.monthlyPrice} x 12 ={' '}
                            <span className="line-through">{formatCad(regularYearlyTotal)}</span>
                          </p>
                          <p className="text-green-600 font-bold text-base sm:text-lg md:text-xl break-words">
                            Yearly total after {selectedPackage.yearlyDiscountPct}% off: {formatCad(discountedYearlyTotal)}
                          </p>
                          <p className="text-green-700 font-semibold text-base sm:text-lg md:text-xl break-words">
                            You save: {formatCad(yearlySavings)}
                          </p>
                        </div>
                      ) : (
                        <p className="text-sm sm:text-base text-muted-foreground break-words">
                          Monthly total: {formatCad(selectedPackage.monthlyPrice)}/month
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button type="button" variant="outline" className="w-1/3 h-11 md:h-14 text-base md:text-lg" onClick={() => setStep(2)} disabled={isLoading}>
                      {t('common_back', 'Back')}
                    </Button>

                    <Button type="submit" className="w-2/3 h-11 md:h-14 text-base md:text-lg" disabled={isLoading}>
                      {isLoading ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" /> {t('signup_creating_account', 'Creating account...')}
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <UserPlus size={18} /> {t('signup_create_account', 'Create account')}
                        </span>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </form>

            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">{t('signup_have_account', 'Already have an account?')} </span>
              <Link to="/login" className="text-primary hover:underline font-medium">
                {t('signup_sign_in', 'Sign in')}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


